import type { ReactNode } from 'react';
import { Link, Navigate } from 'react-router';
import { InstallApp } from '../../components/Notifications';
import { PhonePageHead, usePhoneShell } from '../../components/PhoneApp';
import { ThemeToggle, VersionNote, useStitchAdminUrl, useTenant } from '../../components/Shell';
import { Button } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { Icon, type IconName } from '../../lib/icons';
import { usePresenceList } from '../../lib/messages';
import { MyStatus } from './Messages';

/**
 * The phone app's More tab: you (your status), your own phone, the company's pages your role
 * allows, and the company's admin (admins) folded away, since it's easier on a computer. Then your
 * settings, help, installing the app, and signing out.
 */
export function PhoneMore() {
  const shell = usePhoneShell();
  const { tenant } = useTenant();
  const { state } = useAuth();
  if (!shell || !state?.user) return <Navigate to={`/t/${encodeURIComponent(tenant.domain)}`} replace />;
  return <More shell={shell} myId={state.user.id} name={state.user.name || state.user.email} />;
}

function Row({ to, icon, label, detail, onClick }: { to?: string; icon: IconName; label: string; detail?: string; onClick?: () => void }) {
  const body = (
    <>
      <Icon name={icon} size={19} />
      <span className="grow cell-stack">
        <span>{label}</span>
        {detail && <small className="muted">{detail}</small>}
      </span>
      {to && <Icon name="chevRight" size={16} />}
    </>
  );
  return to ? (
    <Link to={to} className="you-row">
      {body}
    </Link>
  ) : (
    <button type="button" className="you-row" onClick={onClick}>
      {body}
    </button>
  );
}

function Group({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="pmore-group" aria-label={title}>
      {title && <h2 className="pmore-title">{title}</h2>}
      <div className="you-rows">{children}</div>
    </section>
  );
}

function More({ shell, myId, name }: { shell: NonNullable<ReturnType<typeof usePhoneShell>>; myId: string; name: string }) {
  const { base, membership: m, admin } = shell;
  // Stitch staff in the portal of a split Stitch: Stitch Admin, at its own address.
  const stitchAdmin = useStitchAdminUrl();
  const domain = base.replace(/^\/t\//, '');
  const presence = usePresenceList(decodeURIComponent(domain));
  const perms = m?.permissions ?? {};
  const company = {
    wallboard: admin || Boolean(perms.wallboard || perms.agents),
    calls: admin || Boolean(perms.calls),
    reports: admin || Boolean(perms.reports),
    boxes: admin
  };
  return (
    <div className="phome pmore">
      <PhonePageHead title="More" />
      {/* You: how you show (online, do not disturb…) and a status of your own; tap to change. */}
      <div className="pmore-status">
        <MyStatus domain={decodeURIComponent(domain)} myId={myId} name={name} presence={presence} />
        <p className="pmore-who muted">
          {shell.subtitle ? `${shell.subtitle} · ` : ''}
          {shell.company}
        </p>
      </div>

      {m?.extension && (
        <Group title="Your phone">
          <Row to={`${base}/me`} icon="device" label="My phone" detail="Do not disturb, call queues, your desk phone’s keys" />
          <Row to={`${base}/me/calls?show=voicemail`} icon="mail" label="Voicemail" />
          <Row to={`${base}/me/calls`} icon="phone" label="Recent calls" />
          {m.seat === 'receptionist' && <Row to={`${base}/console`} icon="headset" label="Console" detail="Your calls, and everyone you can send them to" />}
        </Group>
      )}

      <Group title="Meetings">
        <Row to={`${base}/meetings`} icon="video" label="Meetings" detail="Start one, or set one up, and send its link to anyone" />
      </Group>

      {(company.wallboard || company.calls || company.reports || company.boxes) && (
        <Group title={shell.company}>
          {company.wallboard && <Row to={`${base}/wallboard`} icon="chart" label="Wallboard" />}
          {company.calls && <Row to={`${base}/calls`} icon="history" label="Call history" />}
          {company.reports && <Row to={`${base}/reports`} icon="report" label="Queue reports" />}
          {company.boxes && <Row to={`${base}/voicemail`} icon="mail" label="Voicemail boxes" />}
        </Group>
      )}

      {admin && shell.adminMenu && (
        <details className="pmore-admin">
          <summary>
            <Icon name="sliders" size={19} />
            <span className="grow cell-stack">
              <span>Company admin</span>
              <small className="muted">Everything for running {shell.company}. Easier on a computer.</small>
            </span>
            <Icon name="chev" size={16} />
          </summary>
          <div className="nav psheet-nav">{shell.adminMenu}</div>
        </details>
      )}

      <Group>
        <Row icon="sliders" label="Your settings" detail="Your photo, notifications, time zone" onClick={shell.openSettings} />
        <Row to="/help" icon="help" label="Help center" />
        {shell.switchCompany && <Row to="/choose" icon="building" label="Switch company" />}
        {stitchAdmin && (
          <a href={`${stitchAdmin}/admin`} target="_blank" rel="noopener" className="you-row">
            <Icon name="server" size={19} />
            <span className="grow cell-stack">
              <span>Stitch Admin</span>
              <small className="muted">Opens in its own tab</small>
            </span>
          </a>
        )}
      </Group>
      <div className="pmore-install">
        <InstallApp />
      </div>
      <div className="pmore-foot">
        <ThemeToggle />
        <Button icon="logout" className="pmore-signout" onClick={shell.signOut}>
          Sign out
        </Button>
        <VersionNote />
      </div>
    </div>
  );
}
